import {
  setup,
  assign,
  forwardTo,
  raise,
  fromPromise,
  fromCallback,
  not,
  and,
  enqueueActions,
  sendTo,
  stateIn,
} from 'xstate';
import { createActorContext } from '@xstate/react';
import Honeybadger from '@honeybadger-io/js';
//import { createBrowserInspector } from '@statelyai/inspect';
import crazyAi from '@crazyegginc/ai';
import mockAI from '../ai/mockAI';
import { PageEditorAPI } from '@crazyegginc/page-editor-api';

import { AddFirstPageModal } from '../editor-ui/components/modals/AddFirstPage';
import { AddPageModal } from '../editor-ui/components/modals/AddPage';
import { SaveChangesModal } from '../editor-ui/components/modals/SaveChanges';
import { HistoryModal } from '../editor-ui/components/modals/History';
import { MobileAccessModal } from '../editor-ui/components/modals/MobileAccess';
import { ReloadPageModal } from '../editor-ui/components/modals/ReloadPage';
import { ErrorLoadingPageModal } from '../editor-ui/components/modals/ErrorLoadingPage';
import { SlowLoadingPageModal } from '../editor-ui/components/modals/SlowLoadingPage';
import { PermissionRequestedModal } from '../editor-ui/components/modals/PermissionRequested';
import { CloseWindowModal } from '../editor-ui/components/modals/CloseWindow';
import { AuthModal } from '../editor-ui/components/modals/Auth';

import { initialContext } from './initialContext';
import { modalMachine, MODAL_ACTIONS } from '../modal/modalMachine';
import { notificationLogic, NOTIFICATION_ACTIONS } from '../notification/notificationLogic';
import { publishMachine } from './publishMachine';
import { authMachine } from './authMachine';
import {
  apiAddPage,
  apiEventListeners,
  broadcastChannelListener,
  apiInit,
  aiInit,
  generateFullPage,
  sharingSessionInit,
  apiSave,
  apiSelectVersion,
  beforeUnloadListener,
  apiPublish,
  apiGivePublishAccess,
  runBrandChecks,
  STATES,
  ACTIONS,
} from '.';
import {
  DEVICES,
  NOTIFICATION_TYPES,
  DEVICE_TO_INT,
  LOADING,
  GENERATING,
  METRICS_PREFIX,
} from '../editor-ui/constants';
import { constructVoiceNameForMetric, sendMetric } from '../utils/metrics';
import { hasSearchParam, getSearchParam, setSearchParam, deleteSearchParam } from '../utils/location';
import { isStaging, isE2E } from '../utils/env';

export const MODAL_MACHINE_ID = 'modalMachineId';
export const NOTIFICATION_LOGIC_ID = 'notificationLogicId';
export const BEFORE_UNLOAD_LISTENER_ID = 'beforeUnloadListenerId';
export const PUBLISH_MACHINE_ID = 'publishMachineId';
export const AUTH_MACHINE_ID = 'authMachineId';
//const { inspect } = createBrowserInspector();

let singleApi;
let singleAI;

function hasPublishedBefore(pages, activePageId) {
  const rootDomain = pages.find((p) => p.id === activePageId)?.domain;
  if (!rootDomain) return false;
  return pages.filter((p) => p.domain === rootDomain).some((p) => p.versions.some((v) => v.published_at != null));
}

const mainMachine = setup({
  delays: {
    pageLoadTimeout: 30000,
  },
  actions: {
    saveStatus: assign({
      editor: ({ event }) => event.detail.editor,
      canUndo: ({ event }) => event.detail.canUndo,
      canRedo: ({ event }) => event.detail.canRedo,
      isDirty: ({ event }) => event.detail.isDirty,
      wordsChanged: ({ event }) => event.detail.wordsChanged,
      imagesChanged: ({ event }) => event.detail.imagesChanged,
      metadata: ({ event }) => event.detail.metadata,
      segment: ({ event }) => event.detail.segment,
      //interactiveMode: ({ event }) => event.detail.interactive,
    }),
    forwardDirtyChanged: sendTo(BEFORE_UNLOAD_LISTENER_ID, ({ event }) => ({
      type: ACTIONS.DIRTY_CHANGED,
      dirty: event.detail.isDirty,
    })),
    saveInitData: assign({
      api: ({ event }) => event.output.api,
      anonymousId: ({ event }) => event.output.anonymousId,
      userId: ({ event }) => event.output.userId,
      userEmail: ({ event }) => event.output.userEmail,
      timeZone: ({ event, context }) => event.output.timeZone ?? context.timeZone,
    }),
    saveAI: assign({
      ai: ({ event }) => event.output.ai,
    }),
    savePageContext: assign({
      pageContext: ({ event }) => event.output.pageContext,
    }),
    savePublishPermissions: assign({
      hasPublishPermissions: ({ event }) => event.output.hasPublishPermissions,
    }),
    saveUser: assign({
      userId: ({ event }) => event.output.userId,
      userEmail: ({ event }) => event.output.userEmail,
      timeZone: ({ event, context }) => event.output.timeZone ?? context.timeZone,
    }),
    saveBrandGuidelines: assign({
      brandGuidelines: ({ event }) => event.output?.brandGuidelines ?? event.brandGuidelines,
    }),
    idToSearchParam: ({ event }) => {
      setSearchParam('editorid', event.output.editor.id);
    },
    saveCapabilities: assign({
      capabilities: ({ event }) => event.output.capabilities,
    }),
    changeDevice: enqueueActions(({ enqueue, context, event }) => {
      enqueue(({ event, context }) => {
        if (event.device === context.device && event.mobile === context.mobile) {
          return;
        }
        event.device === DEVICES.MOBILE
          ? context.api.changeDevice(event.device, event.width, event.dpr)
          : context.api.changeDevice(event.device);
      });
      if (event.device !== context.device) {
        enqueue({
          type: 'internalMetric',
          params: { name: 'device_view_switched', params: { device: DEVICE_TO_INT[event.device] } },
        });
      } else if (event.device == context.device && event.device == DEVICES.MOBILE && context.mobile !== event.mobile) {
        enqueue({
          type: 'internalMetric',
          params: { name: 'mobile_resolution_switched', params: { resolution: event.text } },
        });
      }
      enqueue.assign({
        device: event.device,
        mobile: event.mobile ?? context.mobile,
        interactiveMode: false,
      });
    }),
    apiHidePopups: ({ context }) => {
      context.api.hidePopups();
    },
    apiSetText: ({ event, context }) => {
      context.api.setText(event.text);
    },
    apiUndo: ({ context }) => {
      context.api.undo();
    },
    apiRedo: ({ context }) => {
      context.api.redo();
    },
    apiSelectElement: ({ context, event }) => {
      context.api.selectElement(event.selector);
    },
    apiUnSelectElement: ({ context }) => {
      context.api.selectElement(null);
    },
    apiToggleInteractiveMode: enqueueActions(({ enqueue, context }) => {
      enqueue(() => {
        context.api.toggleInteractive();
      });
      enqueue.assign({
        interactiveMode: !context.interactiveMode,
      });
    }),
    apiCancel: ({ context }) => {
      context.api.cancel();
    },
    apiSetSegment: enqueueActions(({ enqueue, context, event }) => {
      enqueue.assign({
        segmentChanged: true,
      });
      enqueue(() => {
        context.api.setSegment(event.segment, event.description);
      });
      enqueue({
        type: 'internalMetric',
        params: {
          name: 'targeting_set',
          params: { type: event.segmentType.toLowerCase(), details: event.segment ? JSON.parse(event.segment) : {} },
        },
      });
    }),
    apiUpdateBrandGuidelines: enqueueActions(({ enqueue, context, event }) => {
      enqueue(() => {
        context.api.saveBrandGuidelines(event.domain, JSON.stringify(event.brandGuidelines), event.metadata);
      });
    }),
    apiIgnoreCheck: ({ context, event }) => {
      context.api.ignoreCheck(event.checkId, event.el);
    },
    changeVoice: assign({
      voice: ({ event }) => event.voice,
    }),
    elementSelected: enqueueActions(({ enqueue, context, event }) => {
      if (Object.values(context.edited).some(Boolean)) {
        enqueue({
          type: 'internalMetric',
          params: { name: 'element_edited', params: { ...context.edited } },
        });
      }
      enqueue.assign({
        selectedElement: { ...event.el, failedElSelected: context.failedElSelected },
        edited: { ...initialContext.edited },
        failedElSelected: false,
      });
      enqueue({
        type: 'internalMetric',
        params: {
          name: 'element_selected',
          params: { type: event.el.isEditable ? (event.el.imgProps?.url ? 'image' : 'text') : 'group' },
        },
      });
    }),
    elementUnSelected: enqueueActions(({ enqueue, context }) => {
      if (Object.values(context.edited).some(Boolean)) {
        enqueue({
          type: 'internalMetric',
          params: { name: 'element_edited', params: { ...context.edited } },
        });
      }
      enqueue.assign({
        selectedElement: null,
        edited: { ...initialContext.edited },
      });
    }),
    selectedElementUpdated: enqueueActions(({ enqueue, context }) => {
      enqueue.assign({
        selectedElement: ({ event, context }) => ({ ...event.el, failedElSelected: context.failedElSelected }),
      });
      enqueue({
        type: 'runChecksOnElement',
        params: { debounce: !context.shouldRunCheckImmediately },
      });
      enqueue.assign({
        shouldRunCheckImmediately: false,
      });
    }),
    setShouldRunCheckImmediately: assign({
      shouldRunCheckImmediately: ({ event }) => Boolean(event?.ai_fix),
    }),
    saveEditorState: assign({
      editor: ({ event }) => event.output.editor,
    }),
    setActivePage: assign({
      activePageId: ({ event }, params) => params?.pageId ?? event.output.pageId,
      interactiveMode: false,
    }),
    setActiveVersion: assign({
      activeVersionId: (_, params) => params?.versionId ?? null,
      interactiveMode: false,
    }),
    setClean: assign({
      isDirty: false,
    }),
    setSaved: assign({
      saved: true,
    }),
    clearSaved: assign({
      saved: false,
    }),
    setSaving: assign({
      saving: true,
    }),
    clearSaving: assign({
      saving: false,
    }),
    raiseError: enqueueActions(({ enqueue }, params) => {
      enqueue.raise(() => ({
        type: NOTIFICATION_ACTIONS.ADD,
        notification: {
          type: NOTIFICATION_TYPES.ERROR,
          title: params.title,
          text: typeof params.error === 'object' ? params.error.message : params.error,
          extraText: typeof params.error === 'object' ? params.error.stack : null,
        },
      }));
      enqueue(() => {
        Honeybadger.notify(params.error, { context: params.context });
      });
    }),
    honeyBadgerNotify: (_, params) => {
      Honeybadger.notify(params.error, { context: params.context });
    },
    runChecks: enqueueActions(({ enqueue, event, context }) => {
      if (
        event?.type !== ACTIONS.UPDATE_BRAND_GUIDELINES ||
        event?.metadata?.checks_edited.length > 0 ||
        event?.metadata?.checks_enabled_disabled.length > 0 ||
        context.brandCheckResults == null
      ) {
        enqueue.raise({ type: ACTIONS.RUN_CHECKS });
      }
    }),
    runChecksOnElement: enqueueActions(({ enqueue }, params) => {
      if (params?.debounce) {
        enqueue.raise({ type: ACTIONS.RUN_CHECKS_DEBOUNCED_FOR_ELEMENT });
      } else {
        enqueue.raise({ type: ACTIONS.RUN_CHECKS_FOR_ELEMENT });
      }
    }),
    setChecksTargetEl: assign({
      checksTargetEl: ({ context }) => context.selectedElement,
    }),
    clearChecksTargetEl: assign({
      checksTargetEl: null,
    }),
    startBrandChecks: enqueueActions(({ enqueue, context }) => {
      const elId = context.checksTargetEl?.selector ?? 'all';
      if (context.runningCheckRefs[elId]) {
        enqueue.stopChild(({ context }) => context.runningCheckRefs[elId]);
      }
      enqueue.assign({
        runningCheckRefs: ({ spawn }) => ({
          ...context.runningCheckRefs,
          [elId]: spawn('runBrandChecks', {
            input: ({ context, self }) => ({
              api: context.api,
              ai: context.ai,
              checks: context.brandGuidelines.checks,
              el: context.checksTargetEl,
              parentRef: self,
            }),
          }),
        }),
      });
    }),
    saveBrandChecksResults: enqueueActions(({ enqueue, event, context }) => {
      const elId = event.selector ?? 'all';
      // If sender is not the latest check running for this element (or all elements), then ignore results
      if (event.actorId === context.runningCheckRefs[elId]?.id) {
        let results;
        if (!event.selector || !context.brandCheckResults) {
          results = event.results;
        } else {
          results = context.brandCheckResults.filter((r) => r.selector !== event.selector).concat(event.results);
        }
        if (context.selectedElement) {
          // this is for metrics
          const ignoredChecks = context.editor.pages.find((p) => p.id === context.activePageId).ignoredChecks;
          let numFailedChecksForEl = results.filter(
            (r) =>
              r.selector === context.selectedElement.selector &&
              !Object.entries(ignoredChecks).some(([c, s]) => r.check_id === c && s.includes(r.selector)),
          ).length;
          enqueue.assign({
            edited: ({ context }) => ({
              ...context.edited,
              checks_failed_final: numFailedChecksForEl,
              checks_failed_max: Math.max(context.edited.checks_failed_max, numFailedChecksForEl),
            }),
          });
        }
        enqueue.assign({
          brandCheckResults: results,
          runningCheckRefs: ({ context }) => ({
            ...context.runningCheckRefs,
            [elId]: null,
          }),
        });
      }
      enqueue.stopChild(event.actorId);
    }),
    handleBrandCheckError: enqueueActions(({ enqueue, event, context }) => {
      const elId = event.selector ?? 'all';
      if (event.actorId === context.runningCheckRefs[elId]?.id) {
        enqueue.assign({
          runningCheckRefs: ({ context }) => ({
            ...context.runningCheckRefs,
            [elId]: null,
          }),
        });
      }
      enqueue.stopChild(event.actorId);
    }),
    clearBrandChecks: assign({
      brandCheckResults: null,
      runningCheckRefs: {},
      checksTargetEl: null,
      failedElSelected: false,
    }),
    setFailedElSelected: assign({
      failedElSelected: true,
    }),
    closeModal: raise({ type: MODAL_ACTIONS.CLOSE }),
    saveEvent: assign({
      savedEvent: ({ event }) => event,
    }),
    clearSavedEvent: assign({
      savedEvent: null,
    }),
    raiseSavedEvent: enqueueActions(({ enqueue, check, context }) => {
      if (check('hasSavedEvent')) {
        const savedEvent = { ...context.savedEvent };
        enqueue('closeModal');
        enqueue('clearSavedEvent');
        enqueue.raise(savedEvent);
      }
    }),
    internalMetric: ({ context }, { name, params }) => {
      sendMetric(`${METRICS_PREFIX}_${name}`, {
        anonymous_id: context.anonymousId,
        ...(context.editor.user_id ? { user_id: context.editor.user_id } : {}),
        ...params,
      });
    },
    showHistory: enqueueActions(({ enqueue }) => {
      enqueue.raise({ type: MODAL_ACTIONS.SHOW, component: <HistoryModal /> });
      enqueue({
        type: 'internalMetric',
        params: { name: 'history_viewed' },
      });
    }),
    setGeneratorVoiceUsed: assign({
      generatorVoiceUsed: ({ event }) => event.output.generatorVoiceUsed,
    }),
    setSelectedElText: enqueueActions(({ enqueue }) => {
      enqueue.assign({
        doneManualEdits: true,
      });
      enqueue(({ event, context }) => {
        context.api.setText(event.text, null, event.voice ? { voice: event.voice } : null);
      });
    }),
    setSelectedElHTML: enqueueActions(({ enqueue }) => {
      enqueue.assign({
        doneManualEdits: true,
      });
      enqueue(({ event, context }) => {
        context.api.setHTML(event.html, null, event.voice ? { voice: event.voice } : null);
      });
    }),
    setSelectedElAttribute: enqueueActions(({ enqueue }) => {
      enqueue.assign({
        doneManualEdits: true,
      });
      enqueue(({ event, context }) => {
        context.api.editAttribute(event.attribute, event.value, null, event.voice ? { voice: event.voice } : null);
      });
    }),
    setSelectedElImage: enqueueActions(({ enqueue }) => {
      enqueue.assign({
        doneManualEdits: true,
      });
      enqueue(({ event, context }) => {
        context.api.setImage(event.url, null, event.voice ? { voice: event.voice } : null);
      });
    }),
    clearDoneManualEdits: assign({
      doneManualEdits: false,
    }),
    clearGeneratorVoiceUsed: assign({
      generatorVoiceUsed: null,
    }),
    clearSegmentChanged: assign({
      segmentChanged: false,
    }),
    editedAiRephrase: assign({
      edited: ({ context }) => ({ ...context.edited, ai_rephrase: true }),
    }),
    editedAiImage: assign({
      edited: ({ context }) => ({ ...context.edited, ai_image: true }),
    }),
    editedManualImage: assign({
      edited: ({ context }) => ({ ...context.edited, manual_image: true }),
    }),
    trackContentEdited: enqueueActions(({ enqueue, event }) => {
      if (event.content_area === true) {
        enqueue.assign({
          edited: ({ context }) => ({ ...context.edited, content_area: true }),
        });
      } else if (event.ai_fix === true) {
        enqueue.assign({
          edited: ({ context }) => ({ ...context.edited, ai_fix: true }),
        });
      }
    }),
    togglePages: assign({
      pagesOpen: ({ context }) => !context.pagesOpen,
    }),
    toggleVersions: assign({
      versionsOpen: ({ context }) => !context.versionsOpen,
    }),
    toggleControls: assign({
      controlsOpen: ({ context }) => !context.controlsOpen,
    }),
    setVersionsOpen: assign({
      versionsOpen: (_, params) => params?.open,
    }),
    setControlsOpen: assign({
      controlsOpen: (_, params) => params?.open,
    }),
    toggleBrandCheckResults: assign({
      brandCheckResultsOpen: ({ context }) => !context.brandCheckResultsOpen,
    }),
    setBrandCheckResultsOpen: assign({
      brandCheckResultsOpen: (_, params) => params?.open,
    }),
    setUrlToLoad: assign({
      urlToLoad: ({ event }) => event.url,
    }),
    setApiError: assign({
      apiError: (_, params) => params?.error,
    }),
    clearApiError: assign({
      apiError: null,
    }),
    clearUrlToLoad: assign({
      urlToLoad: null,
    }),
    recordPageLoadStartTime: assign({
      pageLoadStartTime: performance.now(),
    }),
    pageLoadedMetric: enqueueActions(({ enqueue, context }) => {
      enqueue({
        type: 'internalMetric',
        params: {
          name: 'page_loaded',
          params: { elapsed_time: Math.round(performance.now() - context.pageLoadStartTime) },
        },
      });
    }),
    downloadApp: () => {
      PageEditorAPI.downloadApp();
    },
    maybeShowApprovalModal: enqueueActions(({ enqueue, check }) => {
      if (check('hasApprovalParams')) {
        enqueue.raise({
          type: MODAL_ACTIONS.SHOW,
          component: <PermissionRequestedModal />,
        });
      }
    }),
    maybeShowInstallSteps: enqueueActions(({ enqueue, check }) => {
      if (check(and(['justLoggedIn', not('hasPublishedBefore'), 'isLoggedIn']))) {
        enqueue.raise({
          type: ACTIONS.SHOW_PUBLISH_STEPS,
        });
      }
      enqueue(() => {
        deleteSearchParam('login');
      });
    }),
  },
  actors: {
    modalMachine: modalMachine,
    notificationLogic: notificationLogic,
    publishMachine: publishMachine,
    authMachine: authMachine,
    apiInit: fromPromise(apiInit),
    sharingSessionInit: fromPromise(sharingSessionInit),
    aiInit: fromPromise(aiInit),
    apiEventListeners: fromCallback(apiEventListeners),
    broadcastChannelListener: fromCallback(broadcastChannelListener),
    apiAddPage: fromPromise(apiAddPage),
    apiSave: fromPromise(apiSave),
    apiSelectVersion: fromPromise(apiSelectVersion),
    beforeUnloadListener: fromCallback(beforeUnloadListener),
    generateFullPage: fromPromise(generateFullPage),
    apiPublish: fromPromise(apiPublish),
    apiGivePublishAccess: fromPromise(apiGivePublishAccess),
    runBrandChecks: fromPromise(runBrandChecks),
  },
  guards: {
    isMobileDevice: () => window.matchMedia('only screen and (max-width: 760px)').matches,
    hasEditorIdSearchParam: () => hasSearchParam('editorid'),
    isSamePage: ({ context, event }) => context.activePageId === event.pageId,
    isSameVersion: ({ context, event }) => context.activeVersionId === event.versionId,
    isDirty: ({ context }) => context.isDirty === true && context.canUndo === true,
    hasSavedEvent: ({ context }) => Boolean(context.savedEvent),
    hasPublishedBefore: ({ context }) => hasPublishedBefore(context.editor.pages, context.activePageId),
    isLoggedIn: ({ context }) => Boolean(context.userId),
    hasPublishPermissions: ({ context }) => context.hasPublishPermissions,
    hasAIClient: ({ context }) => Boolean(context.ai),
    isElementSelected: ({ context }) => Boolean(context.selectedElement),
    hasApprovalParams: () => hasSearchParam('uid') && hasSearchParam('uemail'),
    justLoggedIn: () => hasSearchParam('login'),
    canRunBrandGuidelinesChecks: ({ context }) =>
      Boolean(context.ai && context.brandGuidelines?.updatedAt) &&
      context.brandGuidelines?.checks.filter((c) => c.enabled === true).length > 0,
    isElementCheckable: ({ context }) => context.selectedElement.isCheckable,
    brandCheckForAllElementsRunning: ({ context }) => context.runningCheckRefs?.all?.id != null,
  },
}).createMachine({
  context: { ...initialContext },
  initial: STATES.INIT,
  invoke: [
    {
      src: 'modalMachine',
      id: MODAL_MACHINE_ID,
    },
    {
      src: 'notificationLogic',
      id: NOTIFICATION_LOGIC_ID,
    },
    {
      src: 'broadcastChannelListener',
    },
  ],
  on: {
    [MODAL_ACTIONS.SHOW]: { actions: forwardTo(MODAL_MACHINE_ID) },
    [MODAL_ACTIONS.CLOSE]: { actions: forwardTo(MODAL_MACHINE_ID) },
    [NOTIFICATION_ACTIONS.ADD]: { actions: forwardTo(NOTIFICATION_LOGIC_ID) },
    [NOTIFICATION_ACTIONS.REMOVE]: { actions: forwardTo(NOTIFICATION_LOGIC_ID) },
    [NOTIFICATION_ACTIONS.REMOVE_ALL]: { actions: forwardTo(NOTIFICATION_LOGIC_ID) },
    [ACTIONS.ERROR_DETECTED]: {
      actions: [
        {
          type: 'honeyBadgerNotify',
          params: ({ event }) => ({
            error: event.error,
            context: { apiError: true },
          }),
        },
        raise({
          type: MODAL_ACTIONS.SHOW,
          component: <ReloadPageModal />,
        }),
      ],
    },
    [ACTIONS.DOWNLOAD_APP]: { actions: ['downloadApp'] },
    [ACTIONS.MARK_WINDOW_STALE]: {
      actions: raise({
        type: MODAL_ACTIONS.SHOW,
        component: <CloseWindowModal />,
      }),
    },
  },
  states: {
    [STATES.INIT]: {
      always: [
        {
          guard: 'isMobileDevice',
          target: STATES.MOBILE_ACCESS,
        },
        {
          target: STATES.API_INIT,
        },
      ],
    },
    [STATES.MOBILE_ACCESS]: {
      entry: raise({
        type: MODAL_ACTIONS.SHOW,
        component: <MobileAccessModal />,
      }),
    },
    [STATES.API_INIT]: {
      tags: [LOADING],
      invoke: {
        src: 'apiInit',
        onDone: [
          {
            guard: 'hasEditorIdSearchParam',
            actions: [
              'saveInitData',
              {
                type: 'internalMetric',
                params: { name: 'viewed' },
              },
            ],
            target: STATES.INIT_SHARING_SESSION,
          },
          {
            actions: [
              'saveInitData',
              {
                type: 'internalMetric',
                params: { name: 'viewed' },
              },
            ],
            target: STATES.REQUEST_FIRST_PAGE,
          },
        ],
        onError: {
          actions: [
            {
              type: 'raiseError',
              params: ({ event }) => ({
                title: 'Failed to initialize the Page Editor API.',
                error: event.error,
              }),
            },
          ],
        },
      },
    },
    [STATES.INIT_SHARING_SESSION]: {
      tags: [LOADING],
      invoke: {
        src: 'sharingSessionInit',
        input: ({ context }) => {
          return {
            id: getSearchParam('editorid'),
            pageId: getSearchParam('p'),
            versionId: getSearchParam('v'),
            api: context.api,
          };
        },
        onDone: {
          target: `${STATES.INITIALIZED}.${STATES.GENERAL}.${STATES.SELECTING_VERSION}`,
          actions: ['saveEditorState', 'setActivePage', 'savePageContext', 'saveCapabilities', 'saveBrandGuidelines'],
        },
        onError: {
          actions: [
            {
              type: 'raiseError',
              params: ({ event }) => ({
                title: 'Failed to load the session.',
                error: event.error,
                context: { editorId: getSearchParam('editorid') },
              }),
            },
          ],
        },
      },
    },
    [STATES.REQUEST_FIRST_PAGE]: {
      entry: raise({
        type: MODAL_ACTIONS.SHOW,
        component: <AddFirstPageModal />,
      }),
      on: {
        [ACTIONS.ADD_PAGE]: {
          target: STATES.ADDING_FIRST_PAGE,
        },
      },
    },
    [STATES.ADDING_FIRST_PAGE]: {
      tags: [LOADING],
      entry: [
        'closeModal',
        'setUrlToLoad',
        {
          type: 'internalMetric',
          params: { name: 'first_url_entered' },
        },
        'recordPageLoadStartTime',
      ],
      invoke: {
        src: 'apiAddPage',
        input: ({ event, context }) => ({ url: event.url, api: context.api }),
        onDone: {
          target: `${STATES.INITIALIZED}.${STATES.GENERAL}.${STATES.SELECTING_VERSION}`,
          actions: [
            'saveEditorState',
            'setActivePage',
            'savePageContext',
            'saveBrandGuidelines',
            'idToSearchParam',
            'saveCapabilities',
            'clearUrlToLoad',
            'pageLoadedMetric',
          ],
        },
        onError: {
          actions: [
            {
              type: 'honeyBadgerNotify',
              params: ({ event, context }) => ({
                error: event.error,
                context: { url: context.urlToLoad },
              }),
            },
          ],
          target: STATES.FIRST_PAGE_LOAD_FAILED,
        },
      },
      after: {
        pageLoadTimeout: {
          actions: [
            raise({
              type: MODAL_ACTIONS.SHOW,
              component: <SlowLoadingPageModal />,
            }),
          ],
        },
      },
      on: {
        [ACTIONS.ERROR_MODAL_BACK]: {
          target: STATES.REQUEST_FIRST_PAGE,
          actions: ['apiCancel'],
        },
      },
    },
    [STATES.FIRST_PAGE_LOAD_FAILED]: {
      entry: raise({
        type: MODAL_ACTIONS.SHOW,
        component: <ErrorLoadingPageModal overlayClassName="!bg-gray-500" />,
      }),
      on: {
        [ACTIONS.ERROR_MODAL_BACK]: {
          target: STATES.REQUEST_FIRST_PAGE,
        },
      },
    },
    [STATES.INITIALIZED]: {
      type: 'parallel',
      states: {
        [STATES.GENERAL]: {
          initial: STATES.IDLE,
          invoke: [
            {
              src: 'apiEventListeners',
              input: ({ context }) => ({ api: context.api }),
            },
            {
              src: 'beforeUnloadListener',
              id: BEFORE_UNLOAD_LISTENER_ID,
            },
            {
              src: 'aiInit',
              onDone: {
                actions: ['saveAI', 'runChecks'],
              },
              onError: {
                actions: [
                  {
                    type: 'raiseError',
                    params: ({ event }) => ({
                      title: 'Failed to initialize AI connection.',
                      error: event.error,
                    }),
                  },
                ],
              },
            },
          ],
          on: {
            [ACTIONS.CHANGE_VOICE]: { actions: ['changeVoice'] },
            [ACTIONS.POPUPS_DETECTED]: { actions: ['apiHidePopups'] },
            [ACTIONS.STATUS_CHANGE]: { actions: ['saveStatus', 'forwardDirtyChanged'] },
            [ACTIONS.ELEMENT_LOCKED]: {
              actions: [
                {
                  type: 'internalMetric',
                  params: { name: 'element_locked' },
                },
              ],
            },
            [ACTIONS.TOGGLE_PAGES]: { actions: ['togglePages'] },
            [ACTIONS.TOGGLE_VERSIONS]: { actions: ['toggleVersions'] },
            [ACTIONS.TOGGLE_CONTROLS]: { actions: ['toggleControls'] },
            [ACTIONS.TOGGLE_BRANDCHECKSRESULTS]: { actions: ['toggleBrandCheckResults'] },
            [ACTIONS.UPDATE_BRAND_GUIDELINES]: {
              actions: [
                'saveBrandGuidelines',
                'apiUpdateBrandGuidelines',
                'runChecks',
                {
                  type: 'internalMetric',
                  params: ({ event }) => ({
                    name: 'brand_guidelines_edited',
                    params: {
                      voices_edited: event.metadata.voices_edited.length > 0,
                      voices_organized: event.metadata.voices_organized,
                      checks_edited: event.metadata.checks_edited.length > 0,
                      checks_enabled_disabled: event.metadata.checks_enabled_disabled.length > 0,
                      audience_edited: event.metadata.audience_edited,
                      site_context_edited: event.metadata.site_context_edited,
                      num_visible_voices: event.brandGuidelines.voices.filter((v) => v.enabled === true).length,
                      num_enabled_checks: event.brandGuidelines.checks.filter((c) => c.enabled === true).length,
                    },
                  }),
                },
              ],
            },
            [ACTIONS.IGNORE_CHECKS]: {
              actions: ['apiIgnoreCheck'],
            },
          },
          states: {
            [STATES.IDLE]: {
              entry: ['raiseSavedEvent', 'maybeShowApprovalModal', 'maybeShowInstallSteps'],
              on: {
                [ACTIONS.SHOW_ADD_PAGE]: { target: STATES.ADD_PAGE_MODAL },
                [ACTIONS.ADD_PAGE]: { target: STATES.ADDING_PAGE },
                [ACTIONS.SAVE]: { target: STATES.SAVING },
                [ACTIONS.SAVE_AS_NEW]: { target: STATES.SAVING },
                [ACTIONS.SELECT_PAGE]: [
                  {
                    guard: and([not('isSamePage'), not('isDirty')]),
                    actions: {
                      type: 'setActivePage',
                      params: ({ event }) => ({ pageId: event.pageId }),
                    },
                    target: STATES.SELECTING_VERSION,
                  },
                  {
                    guard: not('isSamePage'),
                    actions: ['saveEvent'],
                    target: STATES.PROMPT_UNSAVED_CHANGES,
                  },
                ],
                [ACTIONS.SELECT_VERSION]: [
                  {
                    guard: and([not('isSameVersion'), not('isDirty')]),
                    target: STATES.SELECTING_VERSION,
                  },
                  {
                    guard: not('isSameVersion'),
                    actions: ['saveEvent'],
                    target: STATES.PROMPT_UNSAVED_CHANGES,
                  },
                ],
                [ACTIONS.CHANGE_DEVICE]: { actions: ['changeDevice'] },
                [ACTIONS.TOGGLE_INTERACTIVE]: { actions: ['apiToggleInteractiveMode'] },
                [ACTIONS.UNDO]: { actions: ['apiUndo'] },
                [ACTIONS.REDO]: { actions: ['apiRedo'] },
                [ACTIONS.SHOW_HISTORY]: { actions: ['showHistory'] },
                [ACTIONS.PUBLISH]: [
                  {
                    // Publish steps completed (PUBLISH action can only happen in this state if it's completed)
                    guard: stateIn({ [STATES.INITIALIZED]: { [STATES.EDITOR]: STATES.PUBLISH_STEPS } }),
                    target: STATES.PUBLISHING,
                  },
                  {
                    guard: and(['hasPublishedBefore', 'isLoggedIn', 'hasPublishPermissions']),
                    target: STATES.PUBLISHING,
                  },
                  {
                    // Targeting a state in the parallel state throws an error. Should work... This is a workaround.
                    actions: [
                      raise({
                        type: ACTIONS.SHOW_PUBLISH_STEPS,
                      }),
                    ],
                  },
                ],
                [ACTIONS.SELECT_ELEMENT]: { actions: ['apiSelectElement'] },
                [ACTIONS.SELECT_FAILED_ELEMENT]: { actions: ['setFailedElSelected', 'apiSelectElement'] },
                [ACTIONS.GENERATE_FULL_PAGE]: {
                  guard: 'hasAIClient',
                  target: STATES.GENERATING_FULL_PAGE,
                },
                [ACTIONS.APPROVE]: { target: STATES.APPROVING },
                [ACTIONS.SET_SEGMENT]: { actions: ['apiSetSegment'] },
                [ACTIONS.SHOW_AUTH_MODAL]: { target: STATES.AUTHENTICATING },
              },
              exit: [
                raise({
                  type: ACTIONS.HIDE_PUBLISH_STEPS,
                }),
              ],
            },
            [STATES.PROMPT_UNSAVED_CHANGES]: {
              entry: raise({
                type: MODAL_ACTIONS.SHOW,
                component: <SaveChangesModal />,
              }),
              on: {
                [ACTIONS.SAVE]: { target: STATES.SAVING },
                [ACTIONS.SAVE_AS_NEW]: { target: STATES.SAVING },
                [ACTIONS.DISCARD]: { actions: ['setClean'], target: STATES.IDLE },
                [ACTIONS.CANCEL]: { actions: ['clearSavedEvent'], target: STATES.IDLE },
              },
            },
            [STATES.ADD_PAGE_MODAL]: {
              entry: [
                raise({
                  type: MODAL_ACTIONS.SHOW,
                  component: <AddPageModal />,
                }),
              ],
              on: {
                [ACTIONS.ADD_PAGE]: [
                  {
                    guard: not('isDirty'),
                    target: STATES.ADDING_PAGE,
                  },
                  {
                    actions: ['saveEvent'],
                    target: STATES.PROMPT_UNSAVED_CHANGES,
                  },
                ],
                [ACTIONS.CANCEL]: { target: STATES.IDLE },
              },
              exit: ['clearApiError'],
            },
            [STATES.ADDING_PAGE]: {
              tags: [LOADING],
              entry: ['closeModal', 'setUrlToLoad', 'elementUnSelected', 'clearSaved', 'recordPageLoadStartTime'],
              invoke: {
                id: 'addPage',
                src: 'apiAddPage',
                input: ({ context, event }) => ({ url: event.url, api: context.api }),
                onDone: {
                  target: STATES.IDLE,
                  actions: [
                    'saveEditorState',
                    'setActivePage',
                    'savePageContext',
                    'saveBrandGuidelines',
                    'setActiveVersion',
                    {
                      type: 'internalMetric',
                      params: { name: 'page_added' },
                    },
                    'clearUrlToLoad',
                    'pageLoadedMetric',
                    'clearBrandChecks',
                    'runChecks',
                  ],
                },
                onError: [
                  {
                    guard: ({ event }) => event.error.message.match(/is already added/),
                    target: STATES.ADD_PAGE_MODAL,
                    actions: [
                      {
                        type: 'setApiError',
                        params: ({ event }) => ({ error: event.error.message }),
                      },
                    ],
                  },
                  {
                    target: STATES.PAGE_LOAD_FAILED,
                    actions: [
                      {
                        type: 'honeyBadgerNotify',
                        params: ({ event, context }) => ({
                          error: event.error,
                          context: { url: context.urlToLoad },
                        }),
                      },
                    ],
                  },
                ],
              },
              after: {
                pageLoadTimeout: {
                  actions: [
                    raise({
                      type: MODAL_ACTIONS.SHOW,
                      component: <SlowLoadingPageModal />,
                    }),
                  ],
                },
              },
              on: {
                [ACTIONS.ERROR_MODAL_BACK]: {
                  target: STATES.ADD_PAGE_MODAL,
                  actions: ['apiCancel'],
                },
              },
            },
            [STATES.PAGE_LOAD_FAILED]: {
              entry: raise({
                type: MODAL_ACTIONS.SHOW,
                component: <ErrorLoadingPageModal />,
              }),
              on: {
                [ACTIONS.ERROR_MODAL_BACK]: {
                  target: STATES.ADD_PAGE_MODAL,
                },
              },
            },
            [STATES.SAVING]: {
              tags: [LOADING],
              entry: ['setSaving', 'elementUnSelected', { type: 'setVersionsOpen', params: { open: true } }],
              invoke: {
                src: 'apiSave',
                input: ({ context, event }) => ({
                  api: context.api,
                  asNew: event.type === ACTIONS.SAVE_AS_NEW,
                  versions: context.editor.pages.find((p) => p.id === context.activePageId)?.versions ?? [],
                  usedVoice: context.generatorVoiceUsed?.voice,
                  doneManualEdits: context.doneManualEdits,
                  segmentChanged: context.segmentChanged,
                  activeVersion: context.editor.pages
                    .find((p) => p.id === context.activePageId)
                    ?.versions.find((v) => v.id === context.activeVersionId),
                }),
                onDone: {
                  target: STATES.IDLE,
                  actions: [
                    {
                      type: 'internalMetric',
                      params: ({ event, context }) => ({
                        name: 'version_saved',
                        params: {
                          saved_as: event.output.asNew,
                          used_generator: !!context.generatorVoiceUsed,
                          voice: context.generatorVoiceUsed
                            ? constructVoiceNameForMetric(context.generatorVoiceUsed)
                            : null,
                          used_editor: context.doneManualEdits,
                        },
                      }),
                    },
                    'saveEditorState',
                    'setSaved',
                    {
                      type: 'setActiveVersion',
                      params: ({ event }) => ({ versionId: event.output.versionId }),
                    },
                    'clearDoneManualEdits',
                    'clearGeneratorVoiceUsed',
                    'clearSegmentChanged',
                    'setClean',
                  ],
                },
                onError: {
                  target: STATES.IDLE,
                  actions: [
                    {
                      type: 'raiseError',
                      params: ({ event }) => ({
                        title: 'Failed to save.',
                        error: event.error,
                      }),
                    },
                  ],
                },
              },
              exit: ['clearSaving'],
            },
            [STATES.SELECTING_VERSION]: {
              tags: [LOADING],
              entry: [
                {
                  type: 'setActiveVersion',
                  params: ({ event, context }) => {
                    let versionId = selectDefaultVersionId({ context });
                    if (event.type === ACTIONS.SELECT_VERSION) {
                      versionId = event.versionId;
                    } else if (event.output?.versionId) {
                      versionId = event.output.versionId;
                    }
                    return { versionId };
                  },
                },
                'elementUnSelected',
                'clearSaved',
              ],
              invoke: {
                src: 'apiSelectVersion',
                input: ({ context }) => ({
                  api: context.api,
                  pageId: context.activePageId,
                  versionId: context.activeVersionId,
                  domain: context.editor.pages.find((p) => p.id === context.activePageId)?.domain,
                  ownerId: context.editor.user_id,
                  userId: context.userId,
                }),
                onDone: {
                  target: STATES.IDLE,
                  actions: [
                    'savePageContext',
                    'savePublishPermissions',
                    'saveBrandGuidelines',
                    'clearBrandChecks',
                    'clearDoneManualEdits',
                    'clearGeneratorVoiceUsed',
                    'clearSegmentChanged',
                    'runChecks',
                  ],
                },
                onError: {
                  target: STATES.IDLE,
                  actions: [
                    {
                      type: 'raiseError',
                      params: ({ event }) => ({
                        title: 'Failed to load version.',
                        error: event.error,
                      }),
                    },
                  ],
                },
              },
            },
            [STATES.PUBLISHING]: {
              tags: [LOADING],
              entry: [{ type: 'setVersionsOpen', params: { open: true } }],
              invoke: {
                src: 'apiPublish',
                input: ({ context }) => ({
                  api: context.api,
                  pageId: context.activePageId,
                  versionId: context.activeVersionId,
                }),
                onDone: {
                  target: STATES.IDLE,
                  actions: [
                    {
                      type: 'internalMetric',
                      params: { name: 'published' },
                    },
                  ],
                },
                onError: {
                  target: STATES.IDLE,
                  actions: [
                    {
                      type: 'raiseError',
                      params: ({ event }) => ({
                        title: 'Failed to publish version.',
                        error: event.error,
                      }),
                    },
                  ],
                },
              },
            },
            [STATES.GENERATING_FULL_PAGE]: {
              tags: [GENERATING],
              entry: ['clearSaved', 'apiUnSelectElement'],
              invoke: {
                src: 'generateFullPage',
                input: ({ context, event }) => ({
                  api: context.api,
                  ai: context.ai,
                  voice: event.voice,
                  voices: context.brandGuidelines.voices,
                  pageContext: context.pageContext,
                  segment: context.segment,
                  brandGuidelines: context.brandGuidelines,
                }),
                onDone: {
                  target: STATES.IDLE,
                  actions: [
                    {
                      type: 'internalMetric',
                      params: ({ event }) => ({
                        name: 'generator_used',
                        params: {
                          voice: constructVoiceNameForMetric(event.output.generatorVoiceUsed),
                          had_locked_elements: event.output.hadLockedElements,
                          elapsed_time: event.output.elapsedTime,
                        },
                      }),
                    },
                    'setGeneratorVoiceUsed',
                    'runChecks',
                  ],
                },
                onError: {
                  target: STATES.IDLE,
                  actions: [
                    {
                      type: 'raiseError',
                      params: ({ event }) => ({
                        title: 'Failed to generate content.',
                        error: event.error,
                      }),
                    },
                  ],
                },
              },
            },
            [STATES.APPROVING]: {
              tags: [LOADING],
              invoke: {
                src: 'apiGivePublishAccess',
                input: ({ context, event }) => ({
                  api: context.api,
                  versionId: event.versionId,
                  pageId: event.pageId,
                  userId: event.userId,
                  userEmail: event.userEmail,
                  redirect: event.redirect,
                  isElectron: event.isElectron,
                }),
                onDone: {
                  target: STATES.IDLE,
                  actions: [
                    raise(({ event }) => ({
                      type: NOTIFICATION_ACTIONS.ADD,
                      notification: {
                        type: NOTIFICATION_TYPES.SUCCESS,
                        title: 'Publish permission approved!',
                        text: `You have granted ${event.output.userEmail} publishing access now and in the future.`,
                      },
                    })),
                  ],
                },
                onError: {
                  target: STATES.IDLE,
                  actions: [
                    {
                      type: 'raiseError',
                      params: ({ event }) => ({
                        title: 'Failed to grant publishing permissions.',
                        error: event.error,
                      }),
                    },
                  ],
                },
              },
            },
            [STATES.AUTHENTICATING]: {
              entry: [
                raise({
                  type: MODAL_ACTIONS.SHOW,
                  component: <AuthModal />,
                }),
              ],
              on: {
                [ACTIONS.CANCEL]: STATES.IDLE,
                [ACTIONS.REPORT_ERROR]: {
                  actions: [
                    {
                      type: 'raiseError',
                      params: ({ event }) => ({
                        title: event.title,
                        error: event.error,
                      }),
                    },
                  ],
                },
                [ACTIONS.TRACK_METRIC]: {
                  actions: [
                    {
                      type: 'internalMetric',
                      params: ({ event }) => ({ ...event.params }),
                    },
                  ],
                },
                [ACTIONS.SET_NEW_USER]: {
                  actions: ['savePublishPermissions', 'saveUser', 'saveCapabilities'],
                },
              },
              invoke: {
                id: AUTH_MACHINE_ID,
                src: 'authMachine',
                input: ({ context, self, event }) => ({
                  api: context.api,
                  userEmail: context.userEmail,
                  userId: context.userId,
                  parentRef: self,
                  editorId: context.editor.id,
                  redirect: event.redirect,
                  onImmediateAuthSuccess: event.onImmediateAuthSuccess,
                }),
                onDone: {
                  actions: [
                    'closeModal',
                    ({ event }) => {
                      event.output.onImmediateAuthSuccess?.();
                    },
                  ],
                  target: STATES.IDLE,
                },
                onError: [
                  {
                    target: STATES.IDLE,
                    actions: [
                      {
                        type: 'honeyBadgerNotify',
                        params: ({ event }) => ({
                          error: event.error,
                          context: {},
                        }),
                      },
                      'closeModal',
                    ],
                  },
                ],
              },
            },
          },
        },
        [STATES.EDITOR]: {
          initial: STATES.HIDDEN,
          on: {
            [ACTIONS.SELECTED_ELEMENT_UPDATED]: { actions: ['selectedElementUpdated'] },
            [ACTIONS.ELEMENT_SELECTED]: {
              actions: ['elementSelected'],
              target: `.${STATES.EDIT}`,
            },
            [ACTIONS.ELEMENT_UNSELECTED]: {
              actions: ['elementUnSelected'],
              target: `.${STATES.HIDDEN}`,
            },
            [ACTIONS.SHOW_PUBLISH_STEPS]: {
              target: `.${STATES.PUBLISH_STEPS}`,
            },
            [ACTIONS.IMAGE_GENERATION_ERROR]: {
              actions: [
                {
                  type: 'raiseError',
                  params: ({ event }) => ({
                    title: 'Failed to generate image suggestions.',
                    error: event.error,
                    context: { image: event.img },
                  }),
                },
              ],
              target: `.${STATES.EDIT}`,
            },
            [ACTIONS.TEXT_GENERATION_ERROR]: {
              actions: [
                {
                  type: 'raiseError',
                  params: ({ event }) => ({
                    title: 'Failed to generate suggestions.',
                    error: event.error,
                    context: { params: event.params },
                  }),
                },
              ],
              target: `.${STATES.EDIT}`,
            },
          },
          states: {
            [STATES.INIT]: {
              always: [
                {
                  guard: 'isElementSelected',
                  target: STATES.EDIT,
                },
                {
                  target: STATES.HIDDEN,
                },
              ],
            },
            [STATES.HIDDEN]: {},
            [STATES.EDIT]: {
              entry: [
                {
                  type: 'setVersionsOpen',
                  params: { open: false },
                },
                {
                  type: 'setControlsOpen',
                  params: ({ context, event }) => ({
                    open:
                      context.selectedElement.failedElSelected || event.type === ACTIONS.BACK_TO_CHECK_RESULTS
                        ? false
                        : true,
                  }),
                },
                {
                  type: 'setBrandCheckResultsOpen',
                  params: ({ context, event }) => ({
                    open:
                      context.selectedElement.failedElSelected || event.type === ACTIONS.BACK_TO_CHECK_RESULTS
                        ? true
                        : false,
                  }),
                },
              ],
              on: {
                [ACTIONS.AI_REPHRASE]: { target: STATES.AI_SUGGESTIONS },
                [ACTIONS.SET_SELECTED_EL_TEXT]: {
                  actions: ['setSelectedElText', 'trackContentEdited', 'clearSaved', 'setShouldRunCheckImmediately'],
                },
                [ACTIONS.SET_SELECTED_EL_HTML]: {
                  actions: ['setSelectedElHTML', 'trackContentEdited', 'clearSaved', 'setShouldRunCheckImmediately'],
                },
                [ACTIONS.SET_SELECTED_EL_ATTRIBUTE]: {
                  actions: ['setSelectedElAttribute', 'trackContentEdited', 'clearSaved'],
                },
                [ACTIONS.SET_SELECTED_EL_IMAGE]: {
                  actions: ['setSelectedElImage', 'editedManualImage', 'clearSaved'],
                },
              },
            },
            [STATES.AI_SUGGESTIONS]: {
              on: {
                [ACTIONS.BACK]: { target: STATES.EDIT },
                [ACTIONS.BACK_TO_CHECK_RESULTS]: { target: STATES.EDIT },
                [ACTIONS.SET_SELECTED_EL_TEXT]: {
                  actions: ['setSelectedElText', 'editedAiRephrase', 'clearSaved'],
                },
                [ACTIONS.SET_SELECTED_EL_HTML]: {
                  actions: ['setSelectedElHTML', 'editedAiRephrase', 'clearSaved'],
                },
                [ACTIONS.SET_SELECTED_EL_ATTRIBUTE]: {
                  actions: ['setSelectedElAttribute', 'editedAiRephrase', 'clearSaved'],
                },
                [ACTIONS.SET_SELECTED_EL_IMAGE]: {
                  actions: ['setSelectedElImage', 'editedAiImage', 'clearSaved'],
                },
              },
            },
            [STATES.PUBLISH_STEPS]: {
              entry: [
                {
                  type: 'setVersionsOpen',
                  params: ({ context }) => {
                    const publishedBefore = hasPublishedBefore(context.editor.pages, context.activePageId);
                    return { open: publishedBefore };
                  },
                },
              ],
              on: {
                [ACTIONS.BACK]: STATES.INIT,
                [ACTIONS.HIDE_PUBLISH_STEPS]: STATES.INIT,
                [ACTIONS.REPORT_ERROR]: {
                  actions: [
                    {
                      type: 'raiseError',
                      params: ({ event }) => ({
                        title: event.title,
                        error: event.error,
                      }),
                    },
                  ],
                },
                [ACTIONS.TRACK_METRIC]: {
                  actions: [
                    {
                      type: 'internalMetric',
                      params: ({ event }) => ({ ...event.params }),
                    },
                  ],
                },
                [ACTIONS.SET_NEW_USER]: {
                  actions: ['savePublishPermissions', 'saveUser', 'saveCapabilities'],
                },
              },
              invoke: {
                id: PUBLISH_MACHINE_ID,
                src: 'publishMachine',
                input: ({ context, self }) => ({
                  api: context.api,
                  userEmail: context.userEmail,
                  userId: context.userId,
                  hasPublishedBefore: hasPublishedBefore(context.editor.pages, context.activePageId),
                  page: context.editor.pages.find((p) => p.id === context.activePageId),
                  parentRef: self,
                  editorId: context.editor.id,
                }),
                onDone: STATES.INIT,
                onError: [
                  {
                    target: STATES.INIT,
                    actions: [
                      {
                        type: 'honeyBadgerNotify',
                        params: ({ event }) => ({
                          error: event.error,
                          context: {},
                        }),
                      },
                    ],
                  },
                ],
              },
            },
          },
        },
        [STATES.BRAND_CHECKS]: {
          initial: STATES.CHECK_IDLE,
          on: {
            [ACTIONS.RUN_CHECKS]: {
              guard: 'canRunBrandGuidelinesChecks',
              target: `.${STATES.START_RUNNING_CHECKS}`,
              actions: ['clearChecksTargetEl'],
            },
            [ACTIONS.RUN_CHECKS_DEBOUNCED_FOR_ELEMENT]: {
              guard: and(['canRunBrandGuidelinesChecks', 'isElementCheckable']),
              target: `.${STATES.CHECK_DEBOUNCE}`,
              actions: ['setChecksTargetEl'],
            },
            [ACTIONS.RUN_CHECKS_FOR_ELEMENT]: [
              {
                guard: and(['canRunBrandGuidelinesChecks', 'isElementCheckable', 'brandCheckForAllElementsRunning']),
                target: `.${STATES.CHECK_DEBOUNCE}`,
                actions: ['setChecksTargetEl'],
              },
              {
                guard: and(['canRunBrandGuidelinesChecks', 'isElementCheckable']),
                target: `.${STATES.START_RUNNING_CHECKS}`,
                actions: ['setChecksTargetEl'],
              },
            ],
            [ACTIONS.SAVE_CHECKS_RESULTS]: {
              actions: ['saveBrandChecksResults'],
            },
            [ACTIONS.BRAND_CHECKS_ERROR]: {
              actions: [
                {
                  type: 'raiseError',
                  params: ({ event }) => ({
                    title: 'Failed to run checks.',
                    error: event.error,
                  }),
                },
                'handleBrandCheckError',
              ],
            },
          },
          states: {
            [STATES.CHECK_IDLE]: {},
            [STATES.CHECK_DEBOUNCE]: {
              after: {
                1000: [
                  {
                    // if a check for all elements is currently running, we'll wait for that to finish before
                    // invoking checks for single elements (to ensure correct order of result handling)
                    guard: 'brandCheckForAllElementsRunning',
                    target: STATES.CHECK_DEBOUNCE,
                    reenter: true,
                  },
                  {
                    target: STATES.START_RUNNING_CHECKS,
                  },
                ],
              },
            },
            [STATES.START_RUNNING_CHECKS]: {
              entry: ['startBrandChecks'],
              always: [
                {
                  target: STATES.CHECK_IDLE,
                },
              ],
            },
          },
        },
      },
    },
  },
});

export const MainMachineContext = createActorContext(mainMachine, {
  //inspect,
});

export function initApi() {
  if (singleApi) {
    return singleApi;
  }
  singleApi = new PageEditorAPI({
    width: '100%',
    height: '100%',
    root: document.querySelector('#page-wrapper'),
    //mode: 'production',
  });

  return singleApi;
}

export async function initCrazyAI() {
  if (singleAI) return singleAI;
  const ai = isE2E() ? mockAI : crazyAi; // mock AI service for e2e tests
  singleAI = await ai.connection({
    version: 3,
    env: isStaging() ? 'stag' : 'prod',
    onError: (error) => {
      console.error(error);
    },
  });

  // Open the socket early.
  try {
    await singleAI.ping();
  } catch (e) {
    //noop
  }

  return singleAI;
}

function selectDefaultVersionId({ context }) {
  let versionId = null;
  const versions = context.editor.pages.find((p) => p.id === context.activePageId).versions;
  if (versions.length > 0) {
    versionId = versions.reduce((acc, v) => (v.updated_at > acc.updated_at ? v : acc), versions[0]).id;
  }

  return versionId;
}
